import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { StatusPill } from "@/components/StatusPill";
import { fmtDate, RUN_STATUS, STATUS_LABELS } from "@/lib/constants";
import { Plus, ClipboardCheck, Lock, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const REVIEW_RESULTS = [
  { value: "bestanden", label: "Bestanden", desc: "Build erfüllt die Prüfkriterien ohne Vorbehalt." },
  { value: "mit_auflagen", label: "Mit Auflagen", desc: "Build erfüllt im Wesentlichen, Auflagen sind in der Übergabe an Audit zu dokumentieren." },
  { value: "ruecklauf", label: "Rücklauf", desc: "Build muss nachgebessert werden. Rücklaufziel ist Pflicht." },
  { value: "nicht_pruefbar", label: "Nicht prüfbar", desc: "Review konnte mangels Vollständigkeit nicht durchgeführt werden." },
];

const FROZEN = (s) => s === "abgeschlossen" || s === "abgelehnt";

const renderResultPill = (result) => {
  if (!result) return <span className="font-mono text-[10px] text-neutral-400">— offen —</span>;
  const map = {
    bestanden: { bg: "#DCFCE7", text: "#166534", border: "#86EFAC" },
    mit_auflagen: { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A" },
    ruecklauf: { bg: "#FEE2E2", text: "#991B1B", border: "#FCA5A5" },
    nicht_pruefbar: { bg: "#F4F4F5", text: "#525252", border: "#D4D4D8" },
  };
  const s = map[result] || map.nicht_pruefbar;
  const label = REVIEW_RESULTS.find((r) => r.value === result)?.label || result;
  return (
    <span
      data-testid={`review-result-${result}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium border rounded-full"
      style={{ background: s.bg, color: s.text, borderColor: s.border }}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: s.text }} />
      {label}
    </span>
  );
};

export default function Reviews() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [reviews, setReviews] = useState([]);
  const [builds, setBuilds] = useState([]);
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [openReviewId, setOpenReviewId] = useState(null);
  const [form, setForm] = useState({ name: "", build_run_id: "", reviewer_id: "", focus: "", notes: "" });

  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      const url = new URL(window.location.href);
      setProjectId(url.searchParams.get("project") || r.data[0]?.project_id || "");
    });
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const load = async (id) => {
    if (!id) return;
    const [r, b] = await Promise.all([
      api.get(`/projects/${id}/reviews`),
      api.get(`/projects/${id}/builds`),
    ]);
    setReviews(r.data);
    setBuilds(b.data);
  };
  useEffect(() => { load(projectId); }, [projectId]);

  const reviewableBuilds = useMemo(() => builds.filter((b) => b.status === "abgeschlossen"), [builds]);

  const userName = (uid) => users.find((u) => u.user_id === uid)?.name || users.find((u) => u.user_id === uid)?.email || uid || "—";

  // Reviewer-Pool: intern_architekt / intern_audit / externer_reviewer (≠ Build-Owner)
  const reviewerCandidates = useMemo(() => {
    const ownerId = builds.find((b) => b.run_id === form.build_run_id)?.owner_id;
    return users.filter((u) => {
      const roles = u.roles || [];
      const eligible = roles.some((r) => ["intern_architekt", "intern_audit", "externer_reviewer", "admin"].includes(r));
      return eligible && u.user_id !== ownerId;
    });
  }, [users, builds, form.build_run_id]);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${projectId}/reviews`, {
        name: form.name,
        build_run_id: form.build_run_id,
        reviewer_id: form.reviewer_id,
        focus: form.focus,
        notes: form.notes,
      });
      toast.success("Review-Lauf gestartet");
      setShowNew(false);
      setForm({ name: "", build_run_id: "", reviewer_id: "", focus: "", notes: "" });
      load(projectId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anlegen fehlgeschlagen");
    }
  };

  const updateField = async (review_id, body) => {
    try {
      await api.patch(`/projects/${projectId}/reviews/${review_id}`, body);
      load(projectId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update fehlgeschlagen");
    }
  };

  const setStatus = async (review_id, status) => {
    try {
      await api.patch(`/projects/${projectId}/reviews/${review_id}`, { status });
      toast.success(`Status: ${STATUS_LABELS[status]}`);
      load(projectId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Statuswechsel fehlgeschlagen");
    }
  };

  const open = reviews.find((r) => r.review_id === openReviewId);

  return (
    <InternalLayout>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Phase 4</div>
          <h1 className="font-display text-3xl md:text-4xl text-neutral-900 flex items-center gap-3">
            Review <ClipboardCheck className="w-6 h-6 text-[#183153]" />
          </h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            Unabhängige Prüfung eines abgeschlossenen Builds. Reviews verändern den Build nicht — sie dokumentieren ein Urteil. Abgeschlossene Reviews sind eingefroren; eine Iteration erfordert einen neuen Review-Lauf.
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          disabled={!projectId || reviewableBuilds.length === 0}
          data-testid="reviews-new-button"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded-sm disabled:opacity-50"
          style={{ background: "#183153" }}
        >
          <Plus className="w-4 h-4" /> Review starten
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          data-testid="reviews-project-select"
          className="px-3 py-2 border border-neutral-300 rounded-sm text-sm"
        >
          <option value="">Projekt wählen…</option>
          {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
        </select>
        {projectId && reviewableBuilds.length === 0 && (
          <span className="text-xs text-amber-700">Mindestens ein Build muss <strong>abgeschlossen</strong> sein, bevor ein Review möglich ist.</span>
        )}
      </div>

      {projectId && (
        <div className="space-y-3">
          {reviews.length === 0 && (
            <div className="bg-white border border-neutral-200 px-5 py-10 text-center text-sm text-neutral-500" data-testid="reviews-empty">
              Noch keine Reviews.
            </div>
          )}
          {reviews.map((rv) => {
            const frozen = FROZEN(rv.status);
            return (
              <div key={rv.review_id} className="bg-white border border-neutral-200" data-testid={`review-${rv.review_id}`}>
                <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-neutral-900 flex items-center gap-2">
                      {frozen && <Lock className="w-3.5 h-3.5 text-neutral-500" />} {rv.name || rv.review_id}
                    </div>
                    <div className="font-mono text-[10px] text-neutral-500">
                      {rv.review_id} · gestartet {fmtDate(rv.started_at)} · beendet {fmtDate(rv.finished_at)} · Reviewer: {userName(rv.reviewer_id)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {renderResultPill(rv.result)}
                    <StatusPill kind="status" value={rv.status} />
                    <button
                      onClick={() => setOpenReviewId(rv.review_id)}
                      data-testid={`review-open-${rv.review_id}`}
                      className="text-xs px-2 py-1 border border-neutral-300 rounded-sm hover:bg-neutral-50"
                    >
                      Details
                    </button>
                  </div>
                </div>

                {/* Audit-Pfad */}
                <div className="px-5 py-2 bg-neutral-50 border-b border-neutral-100 font-mono text-[11px] text-neutral-700 flex flex-wrap gap-1 items-center">
                  <span className="text-neutral-400 uppercase tracking-widest text-[9px]">Audit-Pfad:</span>
                  {rv.discovery_pack_version != null
                    ? <span>Discovery v{rv.discovery_pack_version}</span>
                    : <span className="text-neutral-400">— ohne Discovery —</span>}
                  <span className="text-neutral-400">→</span>
                  <span>{rv.prompt_set_name || rv.prompt_set_id} v{rv.prompt_version}</span>
                  <span className="text-neutral-400">→</span>
                  <span>Build {rv.build_run_name || rv.build_run_id}</span>
                  <span className="text-neutral-400">→</span>
                  <span className="text-neutral-900">Review {rv.review_id}</span>
                </div>

                <div className="px-5 py-3 grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Prüf-Fokus</div>
                    <div className="text-neutral-700 whitespace-pre-wrap">{rv.focus || "—"}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Übergabe vom Build</div>
                    <div className="text-neutral-700 whitespace-pre-wrap text-xs">{rv.build_handover_to_review || "—"}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Begründung Ergebnis</div>
                    <div className="text-neutral-700 whitespace-pre-wrap text-xs">{rv.result_note || "—"}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Anlegen-Modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white border border-neutral-200 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-neutral-200 font-display-medium flex items-center justify-between">
              <span>Neuer Review-Lauf</span>
              <button onClick={() => setShowNew(false)} className="text-neutral-400 hover:text-neutral-700"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={onCreate} className="p-5 space-y-3">
              <input
                required
                placeholder='Review-Name (z. B. „Erstprüfung Build v1")'
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="review-name-input"
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
              />
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Build (nur abgeschlossen) <span className="text-red-500">*</span></label>
                <select
                  required
                  value={form.build_run_id}
                  onChange={(e) => setForm({ ...form, build_run_id: e.target.value, reviewer_id: "" })}
                  data-testid="review-build-select"
                  className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono"
                >
                  <option value="">— wählen —</option>
                  {reviewableBuilds.map((b) => (
                    <option key={b.run_id} value={b.run_id}>
                      {b.name} · v{b.prompt_version} · Owner: {userName(b.owner_id)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Reviewer <span className="text-red-500">*</span></label>
                <select
                  required
                  value={form.reviewer_id}
                  onChange={(e) => setForm({ ...form, reviewer_id: e.target.value })}
                  data-testid="review-reviewer-select"
                  className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  disabled={!form.build_run_id}
                >
                  <option value="">— wählen —</option>
                  {reviewerCandidates.map((u) => (
                    <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
                  ))}
                </select>
                <div className="text-[11px] text-neutral-500 mt-1">Reviewer darf nicht der Build-Owner sein.</div>
              </div>
              <textarea
                rows={2}
                placeholder="Prüf-Fokus (welche Aspekte werden geprüft?)"
                value={form.focus}
                onChange={(e) => setForm({ ...form, focus: e.target.value })}
                data-testid="review-focus-input"
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
              />
              <textarea
                rows={2}
                placeholder="Notizen (optional)"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
              />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Abbrechen</button>
                <button type="submit" data-testid="review-submit-button" className="px-4 py-2 text-sm text-white rounded-sm" style={{ background: "#183153" }}>Starten</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail-Drawer */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setOpenReviewId(null)}>
          <div
            className="bg-white border-l border-neutral-200 w-full max-w-xl h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid={`review-drawer-${open.review_id}`}
          >
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{open.review_id}</div>
                <div className="text-base font-medium text-neutral-900 flex items-center gap-2">
                  {FROZEN(open.status) && <Lock className="w-4 h-4 text-neutral-500" />} {open.name}
                </div>
              </div>
              <button onClick={() => setOpenReviewId(null)} className="text-neutral-400 hover:text-neutral-700"><X className="w-4 h-4" /></button>
            </div>

            <div className="px-5 py-4 space-y-5">
              {FROZEN(open.status) && (
                <div className="px-3 py-2 bg-neutral-50 border border-neutral-200 text-xs text-neutral-700 flex items-start gap-2">
                  <Lock className="w-3.5 h-3.5 mt-0.5 text-neutral-500 flex-shrink-0" />
                  Dieses Review ist eingefroren. Für eine Iteration lege ein neues Review an.
                </div>
              )}

              {/* Bezugskette */}
              <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 font-mono text-[11px] text-neutral-700">
                <div className="text-[9px] uppercase tracking-widest text-neutral-400 mb-1">Audit-Pfad</div>
                <div className="flex flex-wrap gap-1 items-center">
                  {open.discovery_pack_version != null
                    ? <span>Discovery v{open.discovery_pack_version}</span>
                    : <span className="text-neutral-400">— ohne Discovery —</span>}
                  <ArrowRight className="w-3 h-3 text-neutral-400" />
                  <span>{open.prompt_set_name || open.prompt_set_id} v{open.prompt_version}</span>
                  <ArrowRight className="w-3 h-3 text-neutral-400" />
                  <span>Build {open.build_run_name || open.build_run_id}</span>
                  <ArrowRight className="w-3 h-3 text-neutral-400" />
                  <span className="text-neutral-900">Review {open.review_id}</span>
                </div>
                <div className="mt-2 text-[10px] text-neutral-500">
                  Build-Owner: <span className="text-neutral-700">{userName(open.build_owner_id)}</span> · Reviewer: <span className="text-neutral-700">{userName(open.reviewer_id)}</span>
                </div>
              </div>

              {/* Übergabe vom Build (read-only) */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Übergabe vom Build (read-only)</div>
                <div className="text-sm text-neutral-700 whitespace-pre-wrap border border-neutral-200 px-3 py-2 bg-neutral-50">
                  {open.build_handover_to_review || "—"}
                </div>
              </div>

              {/* Reviewer (nur bei nicht-frozen änderbar) */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Reviewer</label>
                {FROZEN(open.status) ? (
                  <div className="text-sm text-neutral-700 mt-1">{userName(open.reviewer_id)}</div>
                ) : (
                  <select
                    value={open.reviewer_id || ""}
                    onChange={(e) => updateField(open.review_id, { reviewer_id: e.target.value })}
                    data-testid="review-reviewer-edit"
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  >
                    <option value="">— wählen —</option>
                    {users.filter((u) => u.user_id !== open.build_owner_id).map((u) => (
                      <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
                    ))}
                  </select>
                )}
                <div className="text-[11px] text-neutral-500 mt-1">Reviewer darf nicht der Build-Owner sein.</div>
              </div>

              {/* Fokus */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Prüf-Fokus</label>
                {FROZEN(open.status) ? (
                  <div className="text-sm text-neutral-700 whitespace-pre-wrap mt-1">{open.focus || "—"}</div>
                ) : (
                  <textarea
                    rows={2}
                    defaultValue={open.focus || ""}
                    onBlur={(e) => updateField(open.review_id, { focus: e.target.value })}
                    data-testid="review-focus-edit"
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  />
                )}
              </div>

              {/* Ergebnis */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Ergebnis</label>
                {FROZEN(open.status) ? (
                  <div className="mt-1">{renderResultPill(open.result)}</div>
                ) : (
                  <div className="mt-1 grid grid-cols-1 gap-1.5">
                    {REVIEW_RESULTS.map((r) => (
                      <label
                        key={r.value}
                        className={`flex items-start gap-2 border px-3 py-2 cursor-pointer text-sm ${
                          open.result === r.value ? "border-[#183153] bg-blue-50" : "border-neutral-200 hover:bg-neutral-50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="result"
                          value={r.value}
                          checked={open.result === r.value}
                          onChange={() => updateField(open.review_id, { result: r.value })}
                          data-testid={`review-result-radio-${r.value}`}
                          className="mt-0.5"
                        />
                        <div>
                          <div className="font-medium text-neutral-900">{r.label}</div>
                          <div className="text-[11px] text-neutral-500">{r.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Begründung */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  Begründung des Ergebnisses {!FROZEN(open.status) && <span className="text-red-500">*</span>}
                </label>
                {FROZEN(open.status) ? (
                  <div className="text-sm text-neutral-700 whitespace-pre-wrap mt-1">{open.result_note || "—"}</div>
                ) : (
                  <textarea
                    rows={3}
                    defaultValue={open.result_note || ""}
                    onBlur={(e) => updateField(open.review_id, { result_note: e.target.value })}
                    data-testid="review-result-note-edit"
                    placeholder="Worauf stützt sich das Urteil? Welche Belege?"
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  />
                )}
              </div>

              {/* Übergabe an Audit (Pflicht bei bestanden / mit_auflagen) */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  Übergabe an Audit
                  {(open.result === "bestanden" || open.result === "mit_auflagen") && !FROZEN(open.status) && <span className="text-red-500"> *</span>}
                </label>
                {FROZEN(open.status) ? (
                  <div className="text-sm text-neutral-700 whitespace-pre-wrap mt-1">{open.handover_to_audit || "—"}</div>
                ) : (
                  <textarea
                    rows={3}
                    defaultValue={open.handover_to_audit || ""}
                    onBlur={(e) => updateField(open.review_id, { handover_to_audit: e.target.value })}
                    data-testid="review-handover-audit-edit"
                    placeholder="Welche Auflagen / Hinweise gehen ans Audit?"
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  />
                )}
              </div>

              {/* Rücklaufziel (Pflicht bei ruecklauf) */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  Rücklaufziel
                  {open.result === "ruecklauf" && !FROZEN(open.status) && <span className="text-red-500"> *</span>}
                </label>
                {FROZEN(open.status) ? (
                  <div className="text-sm text-neutral-700 whitespace-pre-wrap mt-1">{open.rueck_lauf_ziel || "—"}</div>
                ) : (
                  <textarea
                    rows={2}
                    defaultValue={open.rueck_lauf_ziel || ""}
                    onBlur={(e) => updateField(open.review_id, { rueck_lauf_ziel: e.target.value })}
                    data-testid="review-rueck-lauf-edit"
                    placeholder="Welcher Build-/Prompt-/Discovery-Schritt muss korrigiert werden?"
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  />
                )}
              </div>

              {/* Notizen */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Notizen</label>
                {FROZEN(open.status) ? (
                  <div className="text-sm text-neutral-700 whitespace-pre-wrap mt-1">{open.notes || "—"}</div>
                ) : (
                  <textarea
                    rows={2}
                    defaultValue={open.notes || ""}
                    onBlur={(e) => updateField(open.review_id, { notes: e.target.value })}
                    data-testid="review-notes-edit"
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  />
                )}
              </div>

              {/* Status-Steuerung */}
              <div className="border-t border-neutral-200 pt-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Status</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusPill kind="status" value={open.status} />
                  {!FROZEN(open.status) && (
                    <select
                      value={open.status}
                      onChange={(e) => setStatus(open.review_id, e.target.value)}
                      data-testid={`review-status-${open.review_id}`}
                      className="px-2 py-1 text-xs border border-neutral-300 rounded-sm"
                    >
                      {RUN_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  )}
                </div>
                {!FROZEN(open.status) && (
                  <div className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                    Für <strong>abgeschlossen</strong> sind erforderlich: abgeschlossener Build, Reviewer (≠ Build-Owner), gewähltes Ergebnis,
                    nicht-leere Begründung, Übergabe an Audit (bei <em>bestanden</em>/<em>mit_auflagen</em>) bzw. Rücklaufziel (bei <em>Rücklauf</em>).
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </InternalLayout>
  );
}
